import { apiRequest, setTokens, removeTokens } from './api';

// Authentication service
export const authService = {
  // Login
  login: async (email, password) => {
    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        skipAuth: true,
      });
      
      if (response.success) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        // Store user info in localStorage
        localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        return {
          success: true,
          user: response.data.user,
        };
      }
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao fazer login',
      };
    }
  },
  
  // Logout
  logout: async () => {
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeTokens();
      localStorage.removeItem('userInfo');
    }
  },
  
  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await apiRequest('/auth/me');
      
      if (response.success) {
        return response.data.user;
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  },
};

// Cliente service
export const clienteService = {
  // Register new cliente
  register: async (clienteData) => {
    try {
      const response = await apiRequest('/clientes/register', {
        method: 'POST',
        body: JSON.stringify(clienteData),
        skipAuth: true,
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao realizar cadastro',
      };
    }
  },
  
  // Get all clientes (protected)
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/clientes${queryString ? `?${queryString}` : ''}`;
      const response = await apiRequest(endpoint);
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao buscar clientes',
      };
    }
  },
  
  // Get cliente by ID
  getById: async (id) => {
    try {
      const response = await apiRequest(`/clientes/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao buscar cliente',
      };
    }
  },
  
  // Update cliente
  update: async (id, data) => {
    try {
      const response = await apiRequest(`/clientes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao atualizar cliente',
      };
    }
  },
  
  // Update cliente status
  updateStatus: async (id, status) => {
    try {
      const response = await apiRequest(`/clientes/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao atualizar status',
      };
    }
  },
  
  // Add note
  addNote: async (id, text) => {
    try {
      const response = await apiRequest(`/clientes/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao adicionar anotação',
      };
    }
  },
  
  // Get dashboard stats
  getDashboardStats: async () => {
    try {
      const response = await apiRequest('/clientes/stats');
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao buscar estatísticas',
      };
    }
  },
};

// User service
export const userService = {
  // Get all users
  getAll: async () => {
    try {
      const response = await apiRequest('/users');
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao buscar usuários',
      };
    }
  },
  
  // Create user (admin only)
  create: async (userData) => {
    try {
      const response = await apiRequest('/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao criar usuário',
      };
    }
  },
  
  // Update user
  update: async (id, data) => {
    try {
      const response = await apiRequest(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao atualizar usuário',
      };
    }
  },
  
  // Update password
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiRequest('/users/password/update', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao atualizar senha',
      };
    }
  },
  
  // Delete user (admin only)
  delete: async (id) => {
    try {
      const response = await apiRequest(`/users/${id}`, {
        method: 'DELETE',
      });

      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao deletar usuário',
      };
    }
  },
};

// Taxa Selic service
export const taxaSelicService = {
  // Get all taxas
  getAll: async () => {
    try {
      const response = await apiRequest('/taxas-selic');
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao buscar taxas',
      };
    }
  },

  // Create new taxa
  create: async (data) => {
    try {
      const response = await apiRequest('/taxas-selic', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao criar taxa',
      };
    }
  },

  // Update taxa
  update: async (id, data) => {
    try {
      const response = await apiRequest(`/taxas-selic/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao atualizar taxa',
      };
    }
  },

  // Delete taxa
  delete: async (id) => {
    try {
      const response = await apiRequest(`/taxas-selic/${id}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erro ao deletar taxa',
      };
    }
  },
};
